/**
 * Ce que le tableau de bord lit pour les raccourcis de la garderie
 * (`raccourcis.ts`) : les écoles dont l'année scolaire couvre aujourd'hui, leurs
 * plages, le jour férié, et un stage ou une plaine en cours (les vacances).
 * Tout se lit avec les droits ordinaires : le catalogue et les fériés sont
 * ouverts aux comptes connectés, les sessions à Gestion.
 */
import { db } from '../payments/db';
import { sansJpa, stagesDesJpa } from '../jpa/stages.supabase';
import { aujourdHuiBruxelles, heureBruxelles } from './pointage';
import { garderiesDuJour, pasDeGarderie, type ContexteDuJour, type EcoleDeLAnnee, type GarderieDuJour, type PlageDuCatalogue } from './raccourcis';

export interface LectureDesRaccourcis {
  contexte: ContexteDuJour;
  garderies: GarderieDuJour[];
}

const hhmm = (t: string | null | undefined): string => String(t ?? '').slice(0, 5);

export async function lireLesRaccourcisDeLaGarderie(maintenant: Date = new Date()): Promise<LectureDesRaccourcis> {
  const aujourdHui = aujourdHuiBruxelles(maintenant);
  const heure = heureBruxelles(maintenant);
  const vide: ContexteDuJour = { aujourdHui, heure, ecoles: [], plages: [], ferie: false, vacances: false };
  if (pasDeGarderie(vide) === 'week-end') return { contexte: vide, garderies: [] };

  const [annees, plages, feries, jpa] = await Promise.all([
    db
      .from('garderie_annees')
      .select('center_id, annee_scolaire, debut, fin, centre:centers(name, tag)')
      .lte('debut', aujourdHui)
      .gte('fin', aujourdHui),
    db.from('garderie_plages').select('center_id, annee_scolaire, code, libelle, heure_debut, heure_fin, jours').eq('active', true),
    db.from('public_holidays').select('date').eq('date', aujourdHui),
    stagesDesJpa(),
  ]);
  if (annees.error) throw annees.error;
  if (plages.error) throw plages.error;
  if (feries.error) throw feries.error;

  const sessions = await sansJpa(
    db
      .from('sessions')
      .select('id', { count: 'exact', head: true })
      .eq('active', true)
      .eq('archived', false)
      .lte('start_date', aujourdHui)
      .gte('end_date', aujourdHui),
    jpa,
  );
  if (sessions.error) throw sessions.error;

  type Annee = { center_id: string; annee_scolaire: number; debut: string; fin: string; centre: { name: string; tag: string } | { name: string; tag: string }[] | null };
  const ecoles: EcoleDeLAnnee[] = ((annees.data ?? []) as unknown as Annee[]).map((a) => {
    const c = Array.isArray(a.centre) ? a.centre[0] : a.centre;
    return { centerId: a.center_id, nom: c?.name ?? '', tag: c?.tag ?? '', anneeScolaire: a.annee_scolaire, debut: a.debut, fin: a.fin };
  });
  type Plage = { center_id: string; annee_scolaire: number; code: string; libelle: string; heure_debut: string; heure_fin: string; jours: number[] };
  const lesPlages: PlageDuCatalogue[] = ((plages.data ?? []) as Plage[]).map((p) => ({
    centerId: p.center_id,
    anneeScolaire: p.annee_scolaire,
    code: p.code,
    libelle: p.libelle,
    heureDebut: hhmm(p.heure_debut),
    heureFin: hhmm(p.heure_fin),
    jours: (p.jours ?? []).map(Number),
  }));

  const contexte: ContexteDuJour = {
    aujourdHui,
    heure,
    ecoles,
    plages: lesPlages,
    ferie: (feries.data ?? []).length > 0,
    vacances: (sessions.count ?? 0) > 0,
  };
  return { contexte, garderies: garderiesDuJour(contexte) };
}
